import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import get from 'lodash/get';
import find from 'lodash/find';
import { RadioButtonGroup } from '@els/els-react--radio-button';
import { Button } from '@els/els-react--button';
import { Icon } from '@els/els-react--icon';
import { eachHierarchy } from '@Hooks/LibraryInitialization/MoreUnderscore';
import Catalyst from '@Services/Catalyst';
import StructureEditorSwitcher from '@Services/StructureEditorSwitcher';
import Breadcrumb from '@Services/Breadcrumb';
import StructureEditorOptionsStorage from '@Services/AppDBStorage/Storages/StructureEditorOptionsStorage';
import { STRUCTURE_INSERT_MODE_IMPORT } from '@Pages/StructureEditor/constants';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import StructureEditorInstance from '@Containers/StructureEditor/StructureEditorInstance';
import { trackButton } from '@Pages/StructureEditor/analytics';
import StructureOptions, { StructureOptionsBadges } from '@Containers/StructureOptions';
import StructureFromNameModal from './Components/StructureFromNameModal';
import EditorControls from './Components/EditorControls';

import StructureEditorContainer from './StructureEditorContainer';

import './StructureEditorPage.scss';

function StructureEditorPage(props) {
  const {
    structureUID,
    context,
    target: exportTarget,
    previousDatabaseId,
    isDesktop,
    setCurrentEditor,
    setStructureToEditor,
    keyForStructureData,
  } = props;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pasteFromQueryVisible, setPasteFromQueryVisible] = useState(false);
  const [isDesktopStructureOptionsOpen, setIsDesktopStructureOptionsOpen] = useState(false);
  const [editorControlsProps, setEditorControlsProps] = useState({
    onCancel: () => {},
    onClear: () => {},
    onTransfer: () => {},
    transferButtonDisabled: true,
    transferButtonLabel: `Transfer to ${exportTarget !== 'results' ? 'query' : 'filter'}`,
  });
  const [structureName, setStructureName] = useState('');
  const [structure, setStructure] = useState('');
  const [selectedCurrentEditor, setSelectedCurrentEditor] = useState(StructureEditorSwitcher.getCurrentEditor());
  const availableEditors = StructureEditorSwitcher.getAvailableEditors();
  function getStructureFromForm(factItem) {
    let structureFromForm = null;
    let structureNameFromForm = null;
    eachHierarchy(
      factItem.facts,
      (fact) => {
        if (fact && fact.structures && fact.structures.length) {
          structureFromForm = fact.structures[0].structure;
          structureNameFromForm = get(fact, 'structures.0.structureName');
        }
      },
      null,
      { children: 'facts' }
    );
    return { structure: structureFromForm, structureName: structureNameFromForm };
  }

  useEffect(() => {
    // Get db from storage to restore used structure from breadcrumb
    StructureEditorOptionsStorage.get('db')
      .then((databaseId) => {
        if (exportTarget === 'results' || databaseId) {
          setPasteFromQueryVisible(true);
          StructureEditorOptionsStorage.set('db', previousDatabaseId || databaseId);
          const crumb = Breadcrumb.getActiveBreadcrumbForDb(previousDatabaseId || databaseId);
          if (crumb && crumb.queryFactItem && crumb.queryFactItem.structures) {
            const structureFilter = find(crumb.queryFactItem.structures, { id: 'STRUCTURE.QL' });
            setStructure(structureFilter.structure);
            setStructureName(structureFilter.structureName);
          } else if (crumb && crumb.queryFactItem && !crumb.queryFactItem.structures) {
            // handling forms with structure query-let
            const structObj = getStructureFromForm(crumb.queryFactItem);
            setStructure(structObj.structure);
            setStructureName(structObj.structureName);
          } else if (crumb && crumb.quickSearchItem && crumb.quickSearchItem.structure) {
            setStructure(crumb.quickSearchItem.structure);
          }
        }
      })
      .catch(Logger.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCurrentEditor, pasteFromQueryVisible]);

  function addStructureToEditor(structureToAdd, addStructureName, structureInsertMode) {
    // Redux - set structure
    setStructureToEditor({ structure: structureToAdd, structureName: addStructureName, structureInsertMode });
  }

  function showReplaceStructureConfirmPopup() {
    return NotifyDialogsService.openModalWindow({
      title: 'Warning',
      text: 'Do you want to replace the existing structure query?',
      type: 'info',
      cancelText: 'No',
      confirmText: 'Yes',
      onConfirm: () => {
        addStructureToEditor(structure, structureName, STRUCTURE_INSERT_MODE_IMPORT);
      },
      trackingOptions: {
        component: 'Structure editor',
      },
    });
  }

  async function pasteFromQuery() {
    const isEditorEmpty = await (StructureEditorInstance.instance && StructureEditorInstance.instance.isEmpty());
    if (isEditorEmpty) {
      addStructureToEditor(structure, structureName, STRUCTURE_INSERT_MODE_IMPORT);
    } else {
      showReplaceStructureConfirmPopup();
    }
  }

  function selectEditor(event) {
    const editorName = event.target?.value;
    Logger.info(`Selected editor: ${editorName}`);
    Catalyst.trackProductFeature(`rx2:structureeditor:${editorName === 'chemdraw' ? 'chemdraw' : 'marvinjs'}`);
    StructureEditorSwitcher.setCurrentEditor(editorName);
    setSelectedCurrentEditor(editorName);
    setCurrentEditor(editorName);
  }

  function getEditorList() {
    return Object.entries(availableEditors)?.map(([, value]) => ({
      value: value.name,
      label: value.title,
    }));
  }

  function structureFromNameOpenModal() {
    Logger.info('Open structure from name modal');
    trackButton('generate structure from name');
    setIsModalOpen(true);
  }

  function toggleStructureOptions() {
    setIsDesktopStructureOptionsOpen(!isDesktopStructureOptionsOpen);
  }

  return (
    <div
      id="structureEditorPage"
      className={classnames('structure-editor-container', {
        'structure-editor-container--with-side-panel': !isDesktop,
      })}>
      <main
        id="main-content"
        className={classnames('main-block', 'rx-left-gutter', 'rx-right-gutter', {
          'not-tablet-view': !isDesktop,
        })}>
        <div className={classnames('structure-editor-layout', { 'structure-editor-layout--with-sidebar': !isDesktop })}>
          <div className="clearfix u-els-margin-top-1x button-container-height">
            <h1 className="u-els-display-inline-block u-els-float-left u-els-font-size-h4">
              <Icon
                id="select-structure-editor-heading"
                sprite="Eraser"
                isDecorative
                className="u-els-color-secondary u-els-margin-right-1o2"
              />
              Structure editor selected:
            </h1>
            <div className="u-els-float-left">
              <RadioButtonGroup
                className="select-structure-editor u-els-margin-top-1o2"
                options={getEditorList()}
                data-e2e="structure-editor-switcher"
                value={selectedCurrentEditor}
                onChange={(a) => {
                  selectEditor(a);
                }}
                isInline
                a11y={{ name: 'Structure editor selected:' }}
              />
            </div>
            <div className="u-els-float-right">
              {pasteFromQueryVisible && (
                <Button
                  className="u-els-margin-right-1x"
                  data-e2e="paste-from-query-button"
                  onClick={() => pasteFromQuery()}
                  disabled={!structure}
                  type="secondary">
                  Paste from Query
                </Button>
              )}
              <Button
                data-e2e="insert-structure-from-name-button"
                onClick={() => structureFromNameOpenModal()}
                aria-haspopup="true"
                type="secondary">
                Insert structure from name
              </Button>
              {isModalOpen && (
                <StructureFromNameModal
                  closeModal={() => setIsModalOpen(false)}
                  addStructureToEditor={(structureToAdd, addStructureName, structureInsertMode) =>
                    addStructureToEditor(structureToAdd, addStructureName, structureInsertMode)
                  }
                />
              )}
            </div>
          </div>

          {isDesktop && (
            <div className="u-els-background-color-n1 u-els-margin-bottom-1x u-els-padding-1x u-els-width-10o10">
              <div>
                <div className="u-els-display-inline-block">
                  <h2 className="u-els-float-left u-els-margin-bottom-none u-els-font-size-h4 u-els-padding-right-1o2">
                    Search this structure as:
                  </h2>
                  <StructureOptionsBadges id={structureUID} source="structureeditor" />
                </div>
                <div className="u-els-float-right">
                  <Button data-e2e="more-options-modal" type="link" className="u-els-float-right" onClick={() => toggleStructureOptions()}>
                    <Icon
                      id="more-structure-options"
                      className="u-els-font-size-body-large"
                      a11y={{ name: 'More options' }}
                      isVisible
                      isTextFirst
                      sprite="ChevronDown">
                      More options
                    </Icon>
                  </Button>
                </div>
              </div>
              {isDesktopStructureOptionsOpen && (
                <div className="u-els-background-color-n1 u-els-padding-1x">
                  <StructureOptions id={structureUID} isVerticalLayout={!isDesktop} />
                </div>
              )}
            </div>
          )}

          <div className="structure-editor-main">
            <StructureEditorContainer
              editorExportTarget={exportTarget}
              currentEditor={selectedCurrentEditor}
              structureUid={structureUID}
              context={context}
              keyForStructureData={keyForStructureData}
              onControlsReady={!isDesktop ? setEditorControlsProps : undefined}
            />
          </div>
          {!isDesktop && (
            <aside
              className="structure-options-sidebar full-height-scroller u-els-background-color-n1 u-els-padding-1x"
              role="search"
              aria-label="structure search">
              <h2 className="u-els-font-size-h4 u-els-margin-bottom">Search this structure as:</h2>
              <StructureOptions id={structureUID} isVerticalLayout={!isDesktop} />
            </aside>
          )}
          {!isDesktop && editorControlsProps && (
            <div className="structure-editor-controls-slot">
              <EditorControls {...editorControlsProps} />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

StructureEditorPage.propTypes = {
  structureUID: PropTypes.string,
  context: PropTypes.object,
  target: PropTypes.string,
  isDesktop: PropTypes.bool,
  previousDatabaseId: PropTypes.string,
  setCurrentEditor: PropTypes.func,
  setStructureToEditor: PropTypes.func,
  keyForStructureData: PropTypes.string,
};

export default StructureEditorPage;
